<div class="z-table-toolbar">

  @if (exportButtons.length) {
    <div class="z-export-btns" role="group" [attr.aria-label]="'Exportar ' + exportTitle">
      @for (button of exportButtons; track button) {
        <button type="button" (click)="onExport(button)">{{ button }}</button>
      }

      <!-- El portapapeles no da señal visible: sin este aviso, «Copiar» parece
           el mismo botón muerto que era antes. -->
      @if (copiadoTexto) {
        <span class="dt-copied" [class.dt-copied--error]="copiado === 'error'">
          <i class="bi" [class.bi-check2]="copiado === 'ok'"
                        [class.bi-exclamation-triangle]="copiado === 'error'"
             aria-hidden="true"></i>{{ copiadoTexto }}
        </span>
      }
    </div>
  }

  <p class="z-sr-only" role="status" aria-live="polite">{{ copiadoTexto }}</p>

  @if (showLengthSelect || searchable) {
    <div class="dt-controls">

      @if (showLengthSelect) {
        <label class="z-length-select" [attr.for]="lengthId">
          Mostrar
          <select [id]="lengthId"
                  [value]="pageSize"
                  (change)="onLengthChange($any($event.target).value)">
            @for (option of lengthOptions; track option) {
              <option [value]="option">{{ option === 0 ? 'Todos' : option }}</option>
            }
          </select>
          registros
        </label>
      }

      @if (searchable) {
        <label class="z-search" [attr.for]="searchId">
          Buscar:
          <input type="search"
                 [id]="searchId"
                 [value]="search"
                 (input)="onSearch($any($event.target).value)" />
        </label>
      }

    </div>
  }
</div>

@if (showInfo) {
  <div class="z-table-info" aria-live="polite">{{ infoText }}</div>
}

<div class="dt-scroll" #scrollBox>
  <div class="z-table-wrapper" #scroller (scroll)="onScroll()">
    <table class="z-table">
      <thead>
        <tr>
          @if (expandable) {
            <th class="dt-col-toggle" scope="col"><span class="z-sr-only">Detalle</span></th>
          }
          @for (column of columns; track column.key) {
            <th [ngClass]="headerClass(column)"
                [class.sortable]="column.sortable !== false"
                [style.width]="column.width || null"
                [attr.aria-sort]="ariaSort(column)"
                [attr.tabindex]="column.sortable !== false ? 0 : null"
                scope="col"
                (click)="toggleSort(column)"
                (keydown.enter)="toggleSort(column)"
                (keydown.space)="toggleSort(column); $event.preventDefault()">
              {{ column.title }}
              @if (column.sortable !== false) {
                <span class="z-sort">
                  <i class="bi"
                     aria-hidden="true"
                     [ngClass]="sortKey === column.key
                       ? (sortAsc ? 'bi-sort-down-alt' : 'bi-sort-down')
                       : 'bi-arrow-down-up'"></i>
                </span>
              }
            </th>
          }
        </tr>
      </thead>

      <tbody>
        @for (row of paged; track $index; let i = $index) {
          <tr>
            @if (expandable) {
              <td class="z-cell-center">
                <button class="z-row-toggle"
                        type="button"
                        [attr.aria-expanded]="isExpanded(i)"
                        [attr.aria-label]="toggleLabel(i)"
                        (click)="toggleRow(i)">
                  <i class="bi"
                     aria-hidden="true"
                     [ngClass]="isExpanded(i) ? 'bi-caret-down-fill' : 'bi-caret-right-fill'"></i>
                </button>
              </td>
            }

            @for (column of columns; track column.key) {
              <td [ngClass]="cellClass(column, row)">
                @if (column.cellStyle === 'icon') {
                  <button class="z-icon-btn"
                          type="button"
                          [attr.aria-label]="iconLabel(column)"
                          (click)="onIconClick(row, column)">
                    <i class="bi" aria-hidden="true" [ngClass]="column.icon || 'bi-graph-up'"></i>
                  </button>
                } @else {
                  {{ cellText(column, row) }}
                }
              </td>
            }
          </tr>

          @if (expandable && isExpanded(i)) {
            <tr class="z-row-detail">
              <td [attr.colspan]="colspan">
                @if (detailTemplate) {
                  <ng-container *ngTemplateOutlet="detailTemplate; context: { $implicit: row }" />
                } @else {
                  <div class="detail-grid">
                    @for (column of columns; track column.key) {
                      @if (column.cellStyle !== 'icon') {
                        <div class="detail-item">
                          <span class="detail-label">{{ column.title }}:</span>
                          <span class="detail-value">{{ cellText(column, row) }}</span>
                        </div>
                      }
                    }
                  </div>
                }
              </td>
            </tr>
          }
        }

        @if (!paged.length) {
          <tr class="dt-empty-row">
            <td [attr.colspan]="colspan">
              <div class="dt-empty z-rise">
                <i class="bi" aria-hidden="true"
                   [ngClass]="isFiltered ? 'bi-search' : 'bi-inbox'"></i>
                <p class="dt-empty-title">Sin resultados</p>
                <p class="dt-empty-hint">{{ emptyHint }}</p>
              </div>
            </td>
          </tr>
        }
      </tbody>
    </table>
  </div>
</div>

@if (paginated && totalPages > 1) {
  <nav class="z-pagination" aria-label="Paginación de la tabla">
    <button type="button" [disabled]="page === 1" (click)="goTo(page - 1)">Anterior</button>

    @if (compactPagination) {
      <span class="dt-page-of" aria-live="polite">
        <span aria-hidden="true">{{ page }} / {{ totalPages }}</span>
        <span class="z-sr-only">Página {{ page }} de {{ totalPages }}</span>
      </span>
    } @else {
      @for (item of pageWindow; track $index) {
        @if (item === '…') {
          <span class="dt-ellipsis" aria-hidden="true">…</span>
        } @else {
          <button type="button"
                  [class.current]="item === page"
                  [attr.aria-current]="item === page ? 'page' : null"
                  [attr.aria-label]="'Página ' + item"
                  (click)="goTo(item)">{{ item }}</button>
        }
      }
    }

    <button type="button" [disabled]="page === totalPages" (click)="goTo(page + 1)">Siguiente</button>
  </nav>
}
